import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import { IconHelp } from '../IconHelp'
import PublishUtils from "../../../helpers/PublishUtils"

const expectedVars = (
  <>
    <code><b>?value</b></code>{' '}
    <code><b>?label</b></code>{' '}
    <code>?icon</code>{' '}
    <code>?group</code>{' '}
    <code>?groupIcon</code>{' '}
  </>
)

export const configOptions = [
  {
    name: 'query',
    type: 'yasgui',
    label: (
      <span id="query-selectable-results">
        <strong>SPARQL query returning a list of all selectable options.<br /></strong>{' '}
        Expected result variables: {expectedVars}.<br />{' '}
        Other variables may be added as well and will be additionally used for searching.<br />{' '}
        You can leave this empty if you use server-side searching and do not want to load all options upfront.
        </span>
    ),
    helpComponent: IconHelp,
    defaultQuery: `
      SELECT * WHERE {
          VALUES (?value ?group ?groupIcon ?label ?icon ?processor) {
              (<https://apple.com/products/macbook>               "Apple"  "mui-apple" "Apple MacBook"       "mui-laptop-mac" "M4")
              (<https://apple.com/products/macbook-pro>           "Apple"  "mui-apple" "Apple MacBook Pro"   "mui-laptop-mac" "M5")
              (<https://apple.com/products/macbook-air>           "Apple"  "mui-apple" "Apple MacBook Air"   "mui-laptop-mac" "M4")
              (<https://dell.com/products/xps13>                  "Dell"   undef       "Dell XPS 13"         "mui-laptop-windows" "Intel")
              (<https://dell.com/products/xps15>                  "Dell"   undef       "Dell XPS 15"         "mui-laptop-windows" "Intel")
              (<https://dell.com/products/latitude-9440>          "Dell"   undef       "Dell Latitude 9440"  "mui-laptop-windows" "Intel")
              (<https://lenovo.com/products/thinkpad/x1>          "Lenovo" undef       "Lenovo ThinkPad X1"  "mui-laptop-windows" "Intel")
              (<https://lenovo.com/products/thinkpad/t14>         "Lenovo" undef       "Lenovo ThinkPad T14" "mui-laptop-windows" "AMD")
          }
      }
    `
  },
  {
    name: 'searchQuery',
    type: 'yasgui',
    label: (
      <span>
        <strong>SPARQL query for server-side searching using <code>{'{{search}}'}</code><br /></strong>{' '}
        Expected result variables: {expectedVars}.<br />{' '}
        You can leave this query empty if you do not require server-side searching.
      </span>
    ),
    helpComponent: IconHelp,
    defaultQuery: `
      SELECT * WHERE {
          VALUES (?value ?group ?groupIcon ?label ?icon ?processor ?os) {
              (<https://apple.com/products/macbook>               "Apple"  "mui-apple" "Apple MacBook"       "mui-laptop-mac" "M4" "MacOS")
              (<https://apple.com/products/macbook-pro>           "Apple"  "mui-apple" "Apple MacBook Pro"   "mui-laptop-mac" "M5" "MacOS")
              (<https://apple.com/products/macbook-air>           "Apple"  "mui-apple" "Apple MacBook Air"   "mui-laptop-mac" "M4" "MacOS")
              (<https://dell.com/products/xps13>                  "Dell"   undef       "Dell XPS 13"         "mui-laptop-windows" "Intel" "Windows 11 Pro")
              (<https://dell.com/products/xps15>                  "Dell"   undef       "Dell XPS 15"         "mui-laptop-windows" "Intel" "Windows 11 Pro")
              (<https://dell.com/products/latitude-9440>          "Dell"   undef       "Dell Latitude 9440"  "mui-laptop-windows" "Intel" "Windows 11 Pro")
              (<https://lenovo.com/products/thinkpad/x1>          "Lenovo" undef       "Lenovo ThinkPad X1"  "mui-laptop-windows" "Intel" "Windows 11 Pro")
              (<https://lenovo.com/products/thinkpad/t14>         "Lenovo" undef       "Lenovo ThinkPad T14" "mui-laptop-windows" "AMD" "Windows 11 Pro")
          }

          FILTER(
            CONTAINS(LCASE(?label), LCASE("{{search}}"))
            || CONTAINS(LCASE(?processor), LCASE("{{search}}"))
            || CONTAINS(LCASE(?os), LCASE("{{search}}"))
          )
      }
    `
  },
  {
    name: 'labelQuery',
    type: 'yasgui',
    label: (
      <span>
        <strong>SPARQL query for retrieving an option label based on <code>{'{{value}}'}</code><br /></strong>{' '}
        Expected result variable: <code><b>?label</b></code>.<br />{' '}
        You only need this query if: a) you listen to a variable, a) use server-side searching,{' '}
        c) <i>and</i> do not want to use <a href="#query-selectable-results" className="underline">a query returning a list of all selectable options</a>.
      </span>
    ),
    helpComponent: IconHelp,
    defaultQuery: `
      SELECT ?label WHERE {
          VALUES (?value ?label) {
              (<https://apple.com/products/macbook>             "Apple MacBook"       )
              (<https://apple.com/products/macbook-pro>         "Apple MacBook Pro"   )
              (<https://apple.com/products/macbook-air>         "Apple MacBook Air"   )
              (<https://dell.com/products/xps13>                "Dell XPS 13"         )
              (<https://dell.com/products/xps15>                "Dell XPS 15"         )
              (<https://dell.com/products/latitude-9440>        "Dell Latitude 9440"  )
              (<https://lenovo.com/products/thinkpad/x1>        "Lenovo ThinkPad X1"  )
              (<https://lenovo.com/products/thinkpad/t14>       "Lenovo ThinkPad T14" )
          }

          FILTER(?value = <{{value}}>)
      }
    `
  },
  {
    name: 'variant',
    type: 'select',
    label: 'Visual spacing variant. "Big" mimics the spacing of the old SearchAutocomplete widget.',
    options: [
      {
        label: 'Default',
        value: 'default',
      },
      {
        label: 'Big',
        value: 'big'
      },
    ]
  },
  {
    name: 'debounceTime',
    type: 'number',
    label: 'Delay (in milliseconds) after typing before querying results'
  },
  {
    name: 'minSearchLength',
    type: 'number',
    label: 'Minimum amount of characters required before showing/querying results'
  },
  {
    name: 'highlightWords',
    type: 'boolean',
    label: 'Highlight matching text in dropdown results'
  },
  {
    name: 'syncSearchWithLabel',
    type: 'boolean',
    label: "Keep the search input in sync with the selected option's label (you may want to turn this off if the label does not uniquely identify the item)"
  },
  {
    name: 'publishVariable',
    type: 'text',
    label: 'Variable in which the selected option is published',
  },
  {
    name: 'nullSelectionResetVariable',
    type: 'boolean',
    label: `Publish ${PublishUtils.resetUri} when nothing is selected`,
  },
  {
    name: 'subscribeVariable',
    type: 'text',
    label: 'Variable on which to listen to define option selection',
  },
  {
    name: 'lblPlaceholder',
    type: 'text',
    label: 'Label for the search bar placeholder text'
  },
  {
    name: 'lblNoOptionsFound',
    type: 'text',
    label: 'Label for when no options are found'
  },
  {
    name: 'lblLoadingOptions',
    type: 'text',
    label: 'Label for when options are loading'
  },
  {
    name: 'lblErrorFetchingOptions',
    type: 'text',
    label: 'Label for when options failed to load'
  }
] as const

export type SearchSelectConfigOptions = ConfigurablePropsFromDescription<typeof configOptions>
